import React, { FC, useMemo, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Select from "react-select/async";
import { AppGalaxyInfo, AppGalaxyInfoType } from "types/Galaxy";
import { OnChangeValue, GroupBase, StylesConfig } from "react-select";
import Fuse from "fuse.js";
import { useNavigate } from "react-router-dom";

const selectStyles: StylesConfig = {
  container: base =>
    ({
      ...base,
      // largeur fluide entre 11rem (minimum utilisable) et 22rem (max historique)
      width: "clamp(11rem, 20vw, 22rem)",
      minWidth: 0,
      zIndex: 10
    } as any),
  control: (base, state) =>
    ({
      ...base,
      minHeight: 32,
      border: "1px solid var(--system-border-2)",
      borderRadius: 8,
      backgroundColor: "var(--system-surface-2)",
      boxShadow: state.isFocused ? "0 0 0 3px var(--system-focus-ring)" : "none",
      cursor: "text"
    } as any),
  valueContainer: base => ({ ...base, padding: "0 6px 0 10px" } as any),
  placeholder: base => ({ ...base, color: "var(--system-text-3)", fontSize: "0.875rem" } as any),
  singleValue: base => ({ ...base, color: "var(--system-text)", fontSize: "0.875rem" } as any),
  input: base => ({ ...base, color: "var(--system-text)", fontSize: "0.875rem" } as any),
  indicatorSeparator: () => ({ display: "none" } as any),
  dropdownIndicator: base => ({ ...base, color: "var(--system-text-3)", padding: "0 8px" } as any),
  clearIndicator: base => ({ ...base, color: "var(--system-text-3)", padding: "0 4px" } as any),
  menu: base =>
    ({
      ...base,
      borderRadius: 8,
      overflow: "hidden",
      boxShadow: "0 20px 45px var(--system-shadow-drawer)",
      border: "1px solid var(--system-border-2)",
      backgroundColor: "var(--system-surface-2)"
    } as any),
  groupHeading: base =>
    ({
      ...base,
      fontSize: "0.68rem",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "var(--system-text-3)"
    } as any),
  option: (base, state) =>
    ({
      ...base,
      fontSize: "0.875rem",
      backgroundColor: state.isFocused ? "var(--system-hover)" : "var(--system-surface-2)",
      color: state.isFocused ? "var(--system-text)" : "var(--system-text-2)",
      cursor: "pointer"
    } as any)
};

export const SearchGalaxies: FC<{ galaxies: Record<string, AppGalaxyInfo> }> = props => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const fuseRef = useRef<Fuse<AppGalaxyInfo> | null>(null);

  const getFuse = useCallback(function getFuse() {
    if (!fuseRef.current) {
      fuseRef.current = new Fuse<AppGalaxyInfo>([], {
        shouldSort: true,
        threshold: 0.33,

        keys: [
          {
            name: "label",
            weight: 0.8
          },
          { name: "code", weight: 0.2 }
        ]
      });
    }
    return fuseRef.current;
  }, []);

  const galaxiesList = useMemo(() => {
    const validGalaxies = Object.keys(props.galaxies).map(key => props.galaxies[key]);
    const sortedGalaxies = validGalaxies.slice().sort((a, b) => {
      const nameA = a.label.toUpperCase();
      const nameB = b.label.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
    return sortedGalaxies;
  }, [props.galaxies]);

  useEffect(() => {
    const fuse = getFuse();
    fuse.setCollection(galaxiesList);
  }, [getFuse, galaxiesList]);

  const filterGalaxies = useCallback(
    function filterGalaxies(inputValue: string) {
      const fuse = getFuse();

      let list: AppGalaxyInfo[];
      if (inputValue === "") {
        list = galaxiesList;
      } else {
        list = fuse.search(inputValue).map(r => r.item);
        list.sort((a, b) => {
          const nameA = a.label.toUpperCase();
          const nameB = b.label.toUpperCase();
          return nameA.localeCompare(nameB);
        });
      }

      const reducedGalaxies: Record<AppGalaxyInfoType | "OTHERS", GroupBase<AppGalaxyInfo>> = {
        GALAXY: {
          options: [],
          label: t("commun_galaxie")
        },
        HOME: {
          options: [],
          label: t("commun_accueil")
        },
        KANBAN: {
          options: [],
          label: t("commun_kanban")
        },
        UNIVERS: {
          options: [],
          label: t("commun_univers")
        },
        TIMELINE: {
          options: [],
          label: t("commun_scheduler")
        },
        CALENDAR: {
          options: [],
          label: t("commun_calendar")
        },

        OTHERS: {
          options: [],
          label: t("commun_autres")
        }
      };

      for (let galaxy of list) {
        const validType: AppGalaxyInfoType | "OTHERS" =
          galaxy.type === "HOME" ? "UNIVERS" : galaxy.type || "OTHERS";
        if (reducedGalaxies[validType]) {
          (reducedGalaxies[validType].options as any).push(galaxy);
        }
      }
      return [
        reducedGalaxies.UNIVERS,
        reducedGalaxies.GALAXY,
        reducedGalaxies.OTHERS,
        reducedGalaxies.KANBAN,
        reducedGalaxies.CALENDAR,
        reducedGalaxies.TIMELINE
      ];
    },
    [galaxiesList, getFuse, t]
  );

  const defaultGalaxies = useMemo(() => {
    return filterGalaxies("");
  }, [filterGalaxies]);

  function filterGalaxiesCallback(inputValue: string, callback: (param: any) => void) {
    const galaxies = filterGalaxies(inputValue);
    callback(galaxies);
  }

  return (
    <Select
      placeholder={t("commun_recherche")}
      isClearable
      closeMenuOnSelect
      defaultOptions={defaultGalaxies as any}
      loadOptions={filterGalaxiesCallback}
      onChange={(newValue: OnChangeValue<AppGalaxyInfo, false>) => {
        if (newValue) {
          const galaxy = newValue as AppGalaxyInfo;
          galaxy.code && navigate(`/page/${galaxy.code}`);
        }
      }}
      styles={selectStyles}
      getOptionLabel={(item: AppGalaxyInfo) => item.label}
      getOptionValue={(item: AppGalaxyInfo) => item.code}
      formatGroupLabel={item => item.label}
    />
  );
};
